// ============================================================================
// Typed view of the per-client progress report payload.
// ----------------------------------------------------------------------------
// The shared API type `ClientReport` is intentionally loose (Record<string,
// unknown>) — the backend serializes nested engine Results. This file narrows
// that JSON into precise, locally-owned types that mirror EXACTLY what
// `viccofitness/reports.py::client_report_data` produces (verified against the
// serialized payload). Every section carries an `ok` flag for conditional
// rendering ("optional-first"), so the page can render only sections with data.
//
// We keep these types here (not in src/api/types.ts) to honour the ownership
// boundary: this page owns its own folder. `parseReport()` does the one cast
// from the loose API shape to this typed shape at the edge.
// ============================================================================
import type { ClientReport, MetricsSummary, SeriesPoint } from "../../api";

export type ReportPeriod = "all" | "month" | "quarter" | "year";

/** Period values offered in the selector, in display order. */
export const REPORT_PERIODS: ReportPeriod[] = [
  "all",
  "month",
  "quarter",
  "year",
];

export function isReportPeriod(v: string): v is ReportPeriod {
  return (REPORT_PERIODS as string[]).includes(v);
}

/** Keys of the body trend series the backend always emits. */
export type BodyMetricKey = "weight" | "body_fat" | "bmi" | "whr" | "whtr";

/** A before/after delta over the reporting window. */
export interface BodyDelta {
  before: number;
  after: number;
  change: number;
}

export interface BodySection {
  ok: boolean;
  series: Record<BodyMetricKey, SeriesPoint[]>;
  deltas: Partial<Record<BodyMetricKey, BodyDelta>>;
  summary: MetricsSummary;
}

/** A single ratio reading + its risk threshold(s). */
export interface RatioReading {
  value: number;
  threshold: number;
  /** Only WHtR carries an upper "high" band. */
  high_threshold?: number;
  note: string | null;
}

export interface RatiosSection {
  ok: boolean;
  latest: {
    whr?: RatioReading;
    whtr?: RatioReading;
  };
}

export interface StrengthPr {
  exercise_id: number;
  exercise_name: string | null;
  best_1rm: number;
}

export interface StrengthSection {
  ok: boolean;
  prs: StrengthPr[];
  total_tonnage: number | null;
}

export interface AdherenceSection {
  ok: boolean;
  completed: number;
  planned: number;
  completion_pct: number | null;
}

export interface GoalProjection {
  weeks: number;
  /** ISO date string (yyyy-mm-dd) the target is projected to be hit. */
  date: string | null;
}

export interface GoalItem {
  kind: string | null;
  metric_key: string;
  baseline: number | null;
  current: number | null;
  target: number | null;
  target_date: string | null;
  direction: string | null;
  progress_pct: number | null;
  projection: GoalProjection | null;
}

export interface GoalsSection {
  ok: boolean;
  items: GoalItem[];
}

/** The client header carried at the top of the report. */
export interface ReportClient {
  id: number;
  name: string;
  sex: string | null;
  height_cm: number | null;
  occupation: string | null;
  birthday: string | null;
  created_at: string;
}

/** The fully-typed report. */
export interface TypedClientReport {
  client: ReportClient;
  period: ReportPeriod;
  body: BodySection;
  ratios: RatiosSection;
  strength: StrengthSection;
  adherence: AdherenceSection;
  goals: GoalsSection;
}

/**
 * Narrow the loose API payload to the typed report at the page boundary.
 * The backend guarantees this shape; the single cast is contained here.
 */
export function parseReport(raw: ClientReport): TypedClientReport {
  return raw as unknown as TypedClientReport;
}

/** True when at least one section has data worth rendering. */
export function hasAnyData(r: TypedClientReport): boolean {
  return (
    r.body.ok ||
    r.ratios.ok ||
    r.strength.ok ||
    r.adherence.ok ||
    r.goals.ok
  );
}
